import React, { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaClock, FaHeart } from "react-icons/fa";
import { AuthAPI } from "../api/AuthAPI";
import DashboardLayout from "../layouts/DashboardLayout";

const paymentStyles = {
  success: {
    bg: "#DCFCE7",
    color: "#15803D",
  },

  pending: {
    bg: "#FEF3C7",
    color: "#B45309",
  },

  failed: {
    bg: "#FEE2E2",
    color: "#DC2626",
  },
};

const PaymentsHistory = () => {
  const navigate = useNavigate();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line
  }, [page]);

  async function fetchPayments() {
    try {
      setLoading(true);

      const res = await AuthAPI.getallpayments({
        page,
        limit: 6,
      });

      setPayments(res?.data?.data || []);

      const settings = res?.data?.settings || {};

      setHasNext(Boolean(settings.next_page));
      setHasPrev(Boolean(settings.prev_page));
    } catch (err) {
      console.error("Payments fetch error", err);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <DashboardLayout>
    <div className="mt-12 md:mt-0">
      {/* HEADER + TABLE */}

      <div
        style={{
          background: "#fff",
          border: "1px solid #ececec",
          borderRadius: "18px",
          overflow: "hidden",
        }}
        
      >
        {/* HEADER */}

        <div
          style={{
            padding: window.innerWidth < 768 ? "14px 16px" : "16px 26px",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#111827",
              }}
            >
              Payment History
            </h2>
          </div>

          <span
            style={{
              fontSize: "13px",
              color: "#8a8f98",
              fontWeight: "500",
            }}
          >
            {payments.length} payment(s)
          </span>
        </div>

        {/* LIST */}

        {loading && (
          <div style={{ padding: "16px" }}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  height: "64px",
                  borderRadius: "12px",
                  marginBottom: "10px",
                  background: "#f3f4f6",
                }}
                className="animate-pulse"
              />
            ))}
          </div>
        )}

        {!loading &&
          payments.map((item) => {
          const status =
            paymentStyles[item.status?.toLowerCase()] ||
            paymentStyles.pending;

          return (
            <div
              key={item.id}
              onClick={() => navigate(`/dashboard/purchase-history/order/${item.order_id}`)}
              style={{
                padding: window.innerWidth < 768 ? "14px 16px" : "16px 22px",

                borderBottom: "1px solid #f3f4f6",

                display: "flex",

                alignItems: "center",

                justifyContent: "space-between",

                gap: window.innerWidth < 768 ? "10px" : "16px",

                cursor: "pointer",

                transition: "0.3s",
              }}
            >
              {/* LEFT */}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: window.innerWidth < 768 ? "10px" : "14px",

                  flex: 1,
                  minWidth: 0,
                }}
              >
                {/* ICON */}

                <div
                  style={{
                    width: window.innerWidth < 768 ? "36px" : "36px",

                    height: window.innerWidth < 768 ? "36px" : "36px",

                    borderRadius: "14px",

                    background:
                      item.status?.toLowerCase() === "success"
                        ? "#DCFCE7"
                        : item.status?.toLowerCase() === "pending"
                        ? "#FEF3C7"
                        : "#FEE2E2",

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center",

                    flexShrink: 0,
                  }}
                >
                  {item.status?.toLowerCase() === "success" ? (
                    <FaCheckCircle color="#2E7D32" size={13} />
                  ) : item.status?.toLowerCase() === "pending" ? (
                    <FaClock color="#E65100" size={13} />
                  ) : (
                    <FaHeart color="#DC2626" size={18} />
                  )}
                </div>

                {/* DETAILS */}

                <div
                  style={{
                    minWidth: 0,
                  }}
                >
                  <h4
                    style={{
                      fontSize: window.innerWidth < 768 ? "13px" : "14px",

                      fontWeight: "700",

                      color: "#111827",
                    }}
                  >
                    #{item.order?.order_number}
                  </h4>

                  <p
                    style={{
                      fontSize: window.innerWidth < 768 ? "11px" : "12px",

                      color: "#6b7280",
                    }}
                  >
                    {item.paid_at ? formatDate(item.paid_at) : "Not Paid Yet"}
                  </p>

                  <p
                    style={{
                      fontSize: "11px",
                      color: "#9ca3af",
                      marginBottom: 0,
                    }}
                  >
                    {item.payment_gateway?.toUpperCase()}
                  </p>
                </div>
              </div>

              {/* RIGHT */}

              <div
                style={{
                  textAlign: "right",
                  flexShrink: 0,
                }}
              >
                <h3
                  style={{
                    fontSize: window.innerWidth < 768 ? "14px" : "16px",

                    fontWeight: "700",

                    color: "#111827",
                  }}
                >
                  ₹{Number(item.amount).toLocaleString("en-IN")}
                </h3>

                <span
                  style={{
                    background: status.bg,

                    color: status.color,

                    padding: window.innerWidth < 768 ? "4px 10px" : "5px 10px",

                    borderRadius: "999px",

                    fontSize: window.innerWidth < 768 ? "10px" : "11px",

                    fontWeight: "700",

                    textTransform: "capitalize",

                    display: "inline-block",
                  }}
                >
                  {item.status}
                </span>
              </div>
            </div>
          );
        })}

        {/* EMPTY */}

        {!loading && payments.length === 0 && (
          <div
            style={{
              height: "320px",

              display: "flex",

              alignItems: "center",

              justifyContent: "center",

              flexDirection: "column",
            }}
          >
            <Wallet
              size={42}
              color="#d1d5db"
              style={{
                marginBottom: "14px",
              }}
            />

            <h4
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "6px",
              }}
            >
              No Payments Found
            </h4>

            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              Your payment history will appear here.
            </p>
          </div>
        )}
      </div>

      {/* PAGINATION */}

      {!loading && payments.length > 0 && (
          <div
                  style={{
                    padding: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    borderTop: "1px solid #f3f4f6",
                  }}
                >
                  <button
                    disabled={!hasPrev}
                    onClick={() => setPage((p) => p - 1)}
                    style={{
                      padding: "8px 18px",
                      borderRadius: "10px",
                      border: "none",
                      background: hasPrev ? "#db2777" : "#e5e7eb",
                      color: hasPrev ? "#fff" : "#9ca3af",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: hasPrev ? "pointer" : "not-allowed",
                    }}
                  >
                    Prev
                  </button>

                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  >
                    Page {page}
                  </div>

                  <button
                    disabled={!hasNext}
                    onClick={() => setPage((p) => p + 1)}
                    style={{
                      padding: "8px 18px",
                      borderRadius: "10px",
                      border: "none",
                      background: hasNext ? "#db2777" : "#e5e7eb",
                      color: hasNext ? "#fff" : "#9ca3af",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: hasNext ? "pointer" : "not-allowed",
                    }}
                  >
                    Next
                  </button>
                </div>
      )}
    </div>
    </DashboardLayout>
  );
};

export default PaymentsHistory;